import { useEffect, useRef, useState } from 'react';
import { T } from '@design/index';
import { store } from '../state/patientProductionStore';
import { patientApi, type AmbulanceTrip, type AmbulanceEmergencyProfile } from '../api';
import EmergencyHistory from '../components/EmergencyHistory';
import EmergencyCallStrip from '../components/EmergencyCallStrip';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';

/**
 * How bad the patient says it is. Offered, never defaulted: a grade nobody
 * chose is worse than no grade, and the operator can read "unstated" for what
 * it is. The Flutter app offers exactly these three, in these words, so one
 * patient's SOS says the same thing whichever client they raised it from.
 */
const SEVERITIES = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MODERATE', label: 'Moderate' },
] as const;

const EMERGENCY_TYPES = [
  { value: 'CARDIAC', label: 'Cardiac/Chest Pain', bg: '#FEF2F2', fg: '#DC2626', icon: 'heart' },
  { value: 'TRAUMA', label: 'Accident/Trauma', bg: '#FFF7ED', fg: '#EA580C', icon: 'trauma' },
  { value: 'STROKE', label: 'Stroke', bg: '#FAF5FF', fg: '#9333EA', icon: 'stroke' },
  { value: 'RESPIRATORY', label: 'Breathing Difficulty', bg: '#EFF6FF', fg: '#2563EB', icon: 'lungs' },
  { value: 'BURN', label: 'Burns', bg: '#FFFBEB', fg: '#D97706', icon: 'burn' },
  { value: 'OTHER', label: 'Other Emergency', bg: '#F3F4F6', fg: '#6B7280', icon: 'other' },
] as const;

function EmergencyIcon({ type, size = 24 }: { type: string; size?: number }) {
  const s = size;
  const common = { width: s, height: s, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' };
  switch (type) {
    case 'heart':
      return (
        <svg {...common}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" />
        </svg>
      );
    case 'trauma':
      return (
        <svg {...common}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="currentColor" />
        </svg>
      );
    case 'stroke':
      return (
        <svg {...common}>
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="currentColor" />
        </svg>
      );
    case 'lungs':
      return (
        <svg {...common}>
          <path d="M12 4v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M8 12c-2 0-4 1-4 4s2 4 4 4c1.5 0 3-.5 4-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          <path d="M16 12c2 0 4 1 4 4s-2 4-4 4c-1.5 0-3-.5-4-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          <circle cx="12" cy="3" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'burn':
      return (
        <svg {...common}>
          <path d="M12 22c3.5 0 6-2.5 6-6.5 0-4.5-6-11.5-6-11.5S6 11 6 15.5C6 19.5 8.5 22 12 22z" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.65 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.76.29 1.55.52 2.36.65A2 2 0 0 1 22 16.92z" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
  }
}

/**
 * What the patient says is happening, mapped to a vehicle class the fleet
 * register actually has. CARDIAC and TRAUMA are categories the ambulance
 * module names; anything else asks for BLS — the baseline every fleet runs —
 * rather than claiming a level of care nobody assessed. The patient's own
 * words travel in `triage`, so the operator can upgrade it.
 */
const VEHICLE_FOR: Record<string, string> = {
  CARDIAC: 'CARDIAC',
  TRAUMA: 'TRAUMA',
};

export default function EmergencySOS() {
  const [step, setStep] = useState<'select' | 'confirm' | 'tracking'>('select');
  const [emergencyType, setEmergencyType] = useState('');
  const [severity, setSeverity] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AmbulanceTrip | null>(null);
  const [apiSuccess, setApiSuccess] = useState(false);
  const [locationCaptured, setLocationCaptured] = useState<boolean | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * The patient's own emergency details — the ones a crew is shown, and the
   * people to ring. Read here, not only on the trips screen: this is where the
   * patient is when it matters, and a contact they cannot dial is a phone
   * number printed on a page.
   */
  const [profile, setProfile] = useState<AmbulanceEmergencyProfile | null>(null);
  useEffect(() => {
    let alive = true;
    patientApi.ambulance.emergencyProfile()
      .then((p) => { if (alive) setProfile(p); })
      .catch(() => { /* nothing on file — the screen simply shows no card */ });
    return () => { alive = false; };
  }, []);

  /**
   * Where the crew should come to. Not a formality: `/ambulance/requests`
   * requires it, and an ambulance cannot be sent to an unknown location. It
   * is prefilled from the patient's saved address so the fast path stays
   * fast, and stays editable because the emergency is rarely at home.
   */
  const [pickupAddress, setPickupAddress] = useState('');
  useEffect(() => {
    let alive = true;
    patientApi.getProfile()
      .then((pr) => {
        if (!alive) return;
        // The saved address usually already ends with the city; appending it
        // again produced "…, FC Road, Pune, Pune" on the confirm screen.
        const addr = (pr.address ?? '').trim();
        const city = (pr.city ?? '').trim();
        const saved = city && !addr.toLowerCase().includes(city.toLowerCase())
          ? [addr, city].filter(Boolean).join(', ')
          : addr || city;
        // Never overwrite something the patient has already started typing.
        setPickupAddress((cur) => (cur.trim() ? cur : saved));
      })
      .catch(() => { /* no saved address — the patient types one */ });
    return () => { alive = false; };
  }, []);

  const selectedType = EMERGENCY_TYPES.find(t => t.value === emergencyType);

  // Real device position, or null if the browser denies/times out. An SOS
  // must never report a hardcoded position — no location beats a wrong one.
  const getPosition = () => new Promise<GeolocationPosition | null>(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      pos => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });

  /**
   * Raise a REAL ambulance request the fleet portal can accept.
   *
   * This used to call `POST /emergency/request`, which writes an
   * `EmergencyRequest` — a record no fleet service reads. The SOS was stored
   * and audit-logged and then reached nobody, which is why every earlier one
   * in the history below reads "Recorded · no crew sent". A request that can
   * actually bring an ambulance is `POST /ambulance/requests`, the same
   * record Care Services → Ambulance creates and the trips screen tracks.
   */
  const handleSOS = async () => {
    const pickup = pickupAddress.trim();
    if (!pickup) {
      setSubmitError(
        'Where should the ambulance come to? An address is required — a crew cannot be sent to an unknown location.',
      );
      return;
    }
    setLoading(true);
    setSubmitError(null);
    const pos = await getPosition();
    setLocationCaptured(!!pos);

    try {
      const trip = await patientApi.ambulance.book({
        requestType: 'EMERGENCY',
        ambulanceType: VEHICLE_FOR[emergencyType] ?? 'BLS',
        pickup: {
          address: pickup,
          // The device's own position, in the fields the fleet operator's
          // request view actually reads. These used to be written into the
          // triage note as text, which put the coordinates in front of a human
          // instead of on a map.
          ...(pos ? { lat: pos.coords.latitude, lng: pos.coords.longitude } : {}),
        },
        // The crew decides where in an emergency; NEAREST_ER is the server's
        // own mode for exactly that, rather than naming a hospital nobody chose.
        destination: { address: 'Nearest available emergency room', choiceMode: 'NEAREST_ER' },
        triage: {
          conditions: [emergencyType].filter(Boolean),
          // A field of its own, so the dispatch board can show it as a badge
          // and sort on it. Left out entirely when the patient did not grade
          // it — the operator then reads "not stated", which is the truth.
          ...(severity ? { severity } : {}),
          notes: notes.trim() || undefined,
        },
      });
      // Mirror only what the server actually created, keyed by its own id.
      store.createAmbulanceRequest({
        id: trip.id,
        emergencyType,
        ambulanceType: trip.ambulanceType,
        location: pickup,
        destination: trip.destination?.address ?? undefined,
        notes: notes || undefined,
      });
      setResult(trip);
      setApiSuccess(true);
      setStep('tracking');
    } catch {
      setResult(null);
      setApiSuccess(false);
      setStep('tracking');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Watch the request until a fleet takes it.
   *
   * The tracking screen was a snapshot of the reply to the POST: it said
   * "Waiting for a fleet to accept" and went on saying it however long the
   * patient stared at it, because nothing ever asked the server again. A crew,
   * a vehicle and an ETA all appear on the SAME record once a fleet accepts —
   * so this re-reads it every ten seconds and stops as soon as there is an
   * assignment or the request is closed.
   */
  const trackedId = step === 'tracking' && apiSuccess ? result?.id ?? null : null;
  const settled = !!result?.assignment
    || ['COMPLETED', 'CANCELLED', 'REJECTED'].includes((result?.status ?? '').toUpperCase());
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!trackedId || settled) return;
    let alive = true;
    const tick = () => {
      patientApi.ambulance.trip(trackedId)
        .then((fresh) => { if (alive && fresh) setResult(fresh); })
        .catch(() => { /* a failed poll is not news; the next one may work */ });
    };
    pollRef.current = window.setInterval(tick, 10_000);
    return () => {
      alive = false;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [trackedId, settled]);

  /** The patient's own emergency contacts, each one person and each dialable. */
  const contacts = Array.from(new Map((profile?.contacts ?? []).map((c) => [c.phone, c])).values());

  const contactsCard = contacts.length > 0 && (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 16, boxShadow: T.shadow }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>Your emergency contacts</div>
      <div style={{ fontSize: 10.5, color: T.textSec, fontFamily: T.font, marginTop: 3, lineHeight: 1.5 }}>
        Released to the crew that accepts your request. Tap to call one yourself.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {contacts.map((c) => (
          <a
            key={c.phone}
            href={`tel:${c.phone}`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '10px 12px', borderRadius: 12, background: T.ice,
              border: `1px solid ${T.border}`, textDecoration: 'none', fontFamily: T.font,
            }}
          >
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: T.text }}>{c.name}</span>
              <span style={{ display: 'block', fontSize: 10, color: T.textSec }}>
                {[c.relation, c.phone].filter(Boolean).join(' · ')}
              </span>
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap' }}>Call</span>
          </a>
        ))}
      </div>
      {(profile?.bloodGroup || (profile?.allergies.length ?? 0) > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
          {profile?.bloodGroup && (
            <span style={{ fontSize: 10.5, fontWeight: 800, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '4px 10px', fontFamily: T.font }}>
              Blood group {profile.bloodGroup}
            </span>
          )}
          {profile?.allergies.map((a) => (
            <span key={a} style={{ fontSize: 10, fontWeight: 600, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '4px 9px', fontFamily: T.font }}>
              Allergic to {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  // ── Tracking View ──
  if (step === 'tracking') {
    // The real lifecycle of an ambulance request. A request is not a crew:
    // it reaches the fleets on NeoHealthCard and one of them has to accept
    // it, so the step after "Sent" only lights up once one has.
    const accepted = !!result?.assignment;
    const statusSteps = apiSuccess
      ? [
          'Request sent to ambulance fleets',
          accepted ? 'A fleet accepted your request' : 'Waiting for a fleet to accept',
          accepted ? 'Crew details shared with you' : 'Crew assigned once accepted',
          'Also call 112 / 108 in a life-threatening emergency',
        ]
      : ['SOS NOT Submitted', 'Server Unreachable', 'Nothing Was Requested', 'Call 112 / 108 Now'];
    const activeStep = apiSuccess ? (accepted ? 2 : 1) : 0;
    return (
      <div style={{ ...govPageContainer({ maxWidth: 900, padding: '0' }), display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* The state services, whatever this request is doing. First, because
            a request waiting to be accepted is not an ambulance on its way. */}
        <EmergencyCallStrip />

        {/* Success / Fallback Banner */}
        <div style={{
          background: apiSuccess ? '#F0FDF4' : '#FFFBEB',
          border: `1px solid ${apiSuccess ? '#BBF7D0' : '#FDE68A'}`,
          borderRadius: 16,
          padding: 24,
          textAlign: 'center',
        }}>
          <div style={{ margin: '0 auto 12px', width: 48, height: 48 }}>
            {apiSuccess ? (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" />
                <polyline points="22 4 12 14.01 9 11.01" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#D97706" strokeWidth="2" />
                <line x1="12" y1="8" x2="12" y2="12" stroke="#D97706" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="#D97706" />
              </svg>
            )}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: apiSuccess ? '#166534' : '#92400E', fontFamily: T.font }}>
            {apiSuccess ? 'Ambulance requested' : 'Request NOT Submitted'}
          </div>
          <div style={{ fontSize: 11, color: apiSuccess ? '#16A34A' : '#B45309', fontFamily: T.font, marginTop: 4 }}>
            {apiSuccess
              ? `${result?.bookingNumber ? `${result.bookingNumber} · ` : ''}Sent to the ambulance fleets on NeoHealthCard. A fleet has to accept it — this app does not dispatch a crew itself. In a life-threatening emergency, call 108 or 112 as well.`
              : 'The server could not be reached, so nothing was requested and no ambulance is coming from this app. Call 112 or 108 right now.'}
          </div>
          {locationCaptured === false && (
            <div style={{ fontSize: 10, color: '#B45309', fontFamily: T.font, marginTop: 8, fontWeight: 600 }}>
              Device location could not be captured — please share your address with the operator when contacted.
            </div>
          )}
        </div>

        {/* The crew, once a fleet has accepted. Until then this says so
            rather than leaving an empty space that reads as "on its way". */}
        {apiSuccess && (
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 20, boxShadow: T.shadow }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" rx="2" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font }}>Ambulance</span>
            </div>
            {result?.assignment ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Vehicle', val: result.assignment.vehicleNumber },
                  { label: 'Crew', val: result.assignment.crewName },
                  { label: 'Crew phone', val: result.assignment.crewPhone },
                  { label: 'ETA', val: result.assignment.etaMinutes != null ? `${result.assignment.etaMinutes} min` : null, highlight: true },
                ].filter((i) => i.val).map((item) => (
                  <div key={item.label}>
                    <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: item.highlight ? T.teal : T.text, fontFamily: T.font, marginTop: 2 }}>{item.val}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, lineHeight: 1.6 }}>
                No fleet has accepted yet, so there is no vehicle, crew or ETA to show.
                Your request is {result?.status ? `“${result.status.toLowerCase()}”` : 'open'} — it stays on your
                Ambulance screen, where you can follow it or call it off.
              </div>
            )}
            <div style={{ fontSize: 10.5, color: T.textTer, fontFamily: T.font, marginTop: 12, lineHeight: 1.6 }}>
              {[
                // What the operator is actually reading — the server's copy of
                // it, so this shows what was stored, not what was typed.
                result?.triage?.severity ? `You said: ${result.triage.severity.toLowerCase()}` : null,
                `${result?.ambulanceType ? `${result.ambulanceType} requested` : null}`,
                result?.pickup?.address ? `Pickup: ${result.pickup.address}` : null,
                result?.destination?.address ? `Destination: ${result.destination.address}` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
        )}

        {/* Live Status */}
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          padding: 20,
          boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.font }}>Live Status</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {statusSteps.map((s, i) => {
              const active = i <= activeStep;
              const pulsing = i === activeStep;
              const dotColor = active ? (pulsing ? '#FACC15' : '#22C55E') : '#D1D5DB';
              return (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: dotColor,
                    animation: pulsing ? 'pulse 1.5s ease-in-out infinite' : undefined,
                    flexShrink: 0,
                  }} />
                  <span style={{
                    fontSize: 11,
                    fontFamily: T.font,
                    fontWeight: active ? 600 : 400,
                    color: active ? T.text : T.textTer,
                  }}>{s}</span>
                </div>
              );
            })}
          </div>
        </div>

        {contactsCard}

        <div style={{ ...GOV_COMPLIANCE.text }}>
          {apiSuccess
            ? 'Your emergency health card — blood group, allergies and emergency contacts — is released to the crew that accepts this request, and to nobody else.'
            : 'Nothing was sent. In a real emergency, always call 112 or 108 directly.'}
        </div>

        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <span style={{ ...GOV_COMPLIANCE.text }}>
            NeoHealthCard™ · Emergency SOS · Every request is audit logged
          </span>
        </div>

        {/* Pulse animation */}
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      </div>
    );
  }

  // ── Confirm View ──
  if (step === 'confirm') {
    return (
      <div style={{ ...govPageContainer({ maxWidth: 900, padding: '0' }), display: 'flex', flexDirection: 'column', gap: 16 }}>
        <EmergencyCallStrip compact />
        <div style={{
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 16,
          padding: 24,
          textAlign: 'center',
        }}>
          <div style={{ margin: '0 auto 12px', color: '#DC2626' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="#DC2626" strokeWidth="2" fill="none" />
              <line x1="12" y1="9" x2="12" y2="13" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1" fill="#DC2626" />
            </svg>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#991B1B', fontFamily: T.font }}>
            Confirm Emergency SOS
          </div>
          <div style={{ fontSize: 11, color: '#DC2626', fontFamily: T.font, marginTop: 4 }}>
            This sends a request to the ambulance fleets on NeoHealthCard. A fleet has to accept it.
          </div>
        </div>

        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          padding: 20,
          boxShadow: T.shadow,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}>
          <div>
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Emergency Type
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font, marginTop: 2 }}>
              {selectedType?.label}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Severity
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: severity ? T.text : T.textTer, fontFamily: T.font, marginTop: 2 }}>
              {/* Not graded is said as such — the operator reads an honest
                  blank, not a grade this screen picked on the patient's behalf. */}
              {SEVERITIES.find((s) => s.value === severity)?.label ?? 'Not stated'}
            </div>
          </div>
          {notes && (
            <div>
              <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Notes
              </div>
              <div style={{ fontSize: 11, color: T.text, fontFamily: T.font, marginTop: 2 }}>
                {notes}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Pickup address *
            </div>
            <input
              type="text"
              value={pickupAddress}
              onChange={(e) => { setPickupAddress(e.target.value); setSubmitError(null); }}
              placeholder="Where should the ambulance come to?"
              style={{
                width: '100%', boxSizing: 'border-box', marginTop: 4,
                fontFamily: T.font, fontSize: 12, color: T.text,
                background: T.white, border: `1px solid ${submitError ? '#FCA5A5' : T.border}`,
                borderRadius: 10, padding: '10px 12px', outline: 'none',
              }}
            />
            <div style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, marginTop: 4 }}>
              Prefilled from your saved address. Change it if you are somewhere else.
            </div>
            {submitError && (
              <div style={{ fontSize: 10.5, color: '#B91C1C', fontFamily: T.font, marginTop: 4 }}>{submitError}</div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Device location
            </div>
            <div style={{ fontSize: 11, color: T.text, fontFamily: T.font, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth="2" strokeLinecap="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Sent with the request if you allow it, alongside the address above
            </div>
          </div>

          <div>
            <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Destination
            </div>
            <div style={{ fontSize: 11, color: T.text, fontFamily: T.font, marginTop: 2 }}>
              Nearest available emergency room — the crew decides on the way
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => setStep('select')}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 12,
              border: `1px solid ${T.border}`,
              background: T.white,
              color: T.text,
              fontSize: 11,
              fontWeight: 600,
              fontFamily: T.font,
              cursor: 'pointer',
            }}
          >
            Go Back
          </button>
          <button
            onClick={handleSOS}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: 12,
              border: 'none',
              background: loading ? '#FCA5A5' : '#DC2626',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: T.font,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? (
              <span>Sending...</span>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.65 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.76.29 1.55.52 2.36.65A2 2 0 0 1 22 16.92z" />
                </svg>
                Request ambulance
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Select View ──
  return (
    <div style={{ ...govPageContainer({ maxWidth: 900, padding: '0' }), display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ ...GOV_TYPE.pageTitle }}>Emergency SOS</div>
        <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, marginTop: 4 }}>
          Select emergency type and trigger SOS
        </div>
      </div>

      {/* Above the SOS button on purpose: a request here waits for a fleet to
          accept it, and 112/108 do not. */}
      <EmergencyCallStrip />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {EMERGENCY_TYPES.map(t => {
          const selected = emergencyType === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setEmergencyType(t.value)}
              style={{
                background: T.white,
                border: selected ? '2px solid #DC2626' : `1px solid ${T.border}`,
                borderRadius: 16,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                boxShadow: selected ? '0 4px 12px rgba(220,38,38,0.15)' : T.shadow,
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: t.bg,
                color: t.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <EmergencyIcon type={t.icon} size={22} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 600, color: T.text, fontFamily: T.font, textAlign: 'center' }}>
                {t.label}
              </span>
            </button>
          );
        })}
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font, marginBottom: 6 }}>
          How bad is it? (optional)
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SEVERITIES.map((s) => {
            const on = severity === s.value;
            return (
              <button
                key={s.value}
                // Tapping the chosen one again clears it — the patient can take
                // back a grade, and nothing is sent that they did not stand by.
                onClick={() => setSeverity(on ? '' : s.value)}
                style={{
                  padding: '7px 14px', borderRadius: 20, cursor: 'pointer', fontFamily: T.font,
                  fontSize: 11, fontWeight: 700,
                  border: on ? '1px solid #DC2626' : `1px solid ${T.border}`,
                  background: on ? '#FEF2F2' : T.white,
                  color: on ? '#991B1B' : T.textSec,
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font, marginBottom: 6 }}>
          Additional Notes (optional)
        </div>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Describe the emergency situation..."
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 12,
            border: `1px solid ${T.border}`,
            background: T.white,
            fontSize: 11,
            fontFamily: T.font,
            color: T.text,
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <button
        onClick={() => {
          if (!emergencyType) return;
          setStep('confirm');
        }}
        style={{
          width: '100%',
          padding: '16px 24px',
          borderRadius: 16,
          border: 'none',
          background: emergencyType ? '#DC2626' : '#FCA5A5',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          fontFamily: T.font,
          cursor: emergencyType ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          boxShadow: emergencyType ? '0 6px 20px rgba(220,38,38,0.35)' : 'none',
          transition: 'all 0.15s ease',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.65 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.76.29 1.55.52 2.36.65A2 2 0 0 1 22 16.92z" />
        </svg>
        Trigger Emergency SOS
      </button>

      {contactsCard}

      {/* What earlier SOS requests actually led to */}
      <EmergencyHistory />

      <div style={{ ...GOV_COMPLIANCE.text, padding: '0 12px' }}>
        In a real emergency, always call your local emergency number (112/108). This feature is for ambulance dispatch support.
      </div>

      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <span style={{ ...GOV_COMPLIANCE.text }}>
          NeoHealthCard™ · Emergency SOS · A fleet has to accept your request · Every request is audit logged
        </span>
      </div>
    </div>
  );
}
